import { Component, Input, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import {
  BehaviorSubject,
  finalize,
  Observable,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import {
  ErrorDetail,
  ErrorResponse,
} from '../../../../../core/models/error.models';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ManageOrganizationService } from '../../../manage-organization.service';
import { Role } from '../../../../../shared/services/role.service';
import { CommonModule } from '@angular/common';
import { ManageOrganizationDrawerParams } from '../../../manage-organization.interface';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { OrgManagementMode } from '../../../manage-organization.enums';
import { CommonService } from '../../../../../shared/services/common.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { LoadingContentComponent } from '../../../../../shared/loading-content/loading-content.component';
import { UploadProgressComponent } from '../../../../../components/upload-progress/upload-progress.component';
import { UploadEvent } from '../../../../../core/services/upload-progress.service';

@Component({
  selector: 'app-import-users',
  standalone: true,
  imports: [
    NzFormItemComponent,
    FormsModule,
    NzFormControlComponent,
    NzFormControlComponent,
    NzFormLabelComponent,
    NzSelectModule,
    NzUploadModule,
    SvgIconComponent,
    NzButtonModule,
    CommonModule,
    ReactiveFormsModule,
    NzSpinModule,
    NzIconModule,
    LoadingContentComponent,
    UploadProgressComponent,
  ],
  templateUrl: './import-users.component.html',
  styleUrl: './import-users.component.scss',
})
export class ImportUsersComponent implements OnDestroy {
  @Input() roleList: Observable<Role[]> | null = null;
  @Input() isLoading!: Observable<boolean>;

  userForm: FormGroup;
  fileList: NzUploadFile[] = [];
  uploading = false;
  private errorsSubject = new BehaviorSubject<ErrorDetail[]>([]);
  errors$ = this.errorsSubject.asObservable();
  private readonly destroy$ = new Subject<void>();
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.CREATE,
  };

  private progressSubject = new BehaviorSubject<number>(0);
  uploadProgress$ = this.progressSubject.asObservable();

  loading$: Observable<boolean>;

  constructor(
    private fb: FormBuilder,
    private message: NzMessageService,
    private manageOrgService: ManageOrganizationService,
    private readonly drawerService: DrawerService,
    private commonService: CommonService,
    private loaderService: LoaderService
  ) {
    this.userForm = this.fb.group({
      roleIds: [null],
      file: [null, [Validators.required]],
    });
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    if (file.type !== 'text/csv') {
      this.message.error('You can only upload CSV files');
      return false;
    }

    this.fileList = [file];
    return false;
  };

  submitForm = (): void => {
    const parentOrgId = this.orgParams.organizationId;
    const roleIds = this.userForm.get('roleIds')?.value;

    if (!roleIds) {
      this.message.error('Please select role');
      return;
    }

    if (this.fileList.length === 0) {
      this.message.error('Please select users csv file');
      return;
    }

    this.loaderService.show();

    this.manageOrgService
      .bulkCreateUsers(
        this.fileList[0] as unknown as File,
        parentOrgId,
        roleIds
      )
      .pipe(
        tap((event: UploadEvent<void>) => {
          if (event.status === 'progress') {
            this.progressSubject.next(event.percentage);
          } else if (event.status === 'completed') {
            this.progressSubject.next(100);
          }
        }),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (event: UploadEvent<void>) => {
          if (event.status === 'completed') {
            this.commonService.notify('success', `User import successfully`);
            this.userForm.reset();
            this.closeImportDrawer();
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          if (message === 'Request Timeout') {
            this.progressSubject.next(80);
          } else {
            this.progressSubject.next(0);
          }

          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify(
            'error',
            errorMessages.length
              ? errorMessages
              : 'Please check your CSV file format and try again',
            message
          );
        },
      });
  };

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  closeImportDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.USER);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
